/**
 * Log Service
 * Manages sending client-side logs to the backend
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class LogService {
  constructor() {
    this.logQueue = [];
    this.isProcessing = false;
    this.maxQueueSize = 50;
  }

  /**
   * Send a log to the backend
   */
  async sendLog(level, message, error = null, additionalData = {}) {
    try {
      const token = localStorage.getItem('authToken');
      
      // Se non c'è token, non possiamo inviare il log (endpoint autenticato)
      if (!token) {
        console.warn('Cannot send log: user not authenticated');
        return;
      }

      const logData = {
        level: level,
        message: message,
        stackTrace: error?.stack || null,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        additionalData: {
          ...additionalData,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          platform: navigator.platform,
          language: navigator.language,
        }
      };

      // Invia il log al backend
      await axios.post(`${API_BASE_URL}/api/log`, logData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        timeout: 5000
      });

    } catch (err) {
      // Non vogliamo generare un loop infinito di errori
      console.error('Failed to send log to backend:', err);
    }
  }

  /**
   * Log an error
   */
  logError(message, error = null, additionalData = {}) {
    console.error(message, error);
    this.sendLog('Error', message, error, additionalData);
  }

  /**
   * Log a warning
   */
  logWarning(message, additionalData = {}) {
    console.warn(message);
    this.sendLog('Warning', message, null, additionalData);
  }

  /**
   * Log info
   */
  logInfo(message, additionalData = {}) {
    console.info(message);
    this.sendLog('Info', message, null, additionalData);
  }

  /**
   * Log debug information
   */
  logDebug(message, additionalData = {}) {
    console.debug(message);
    this.sendLog('Debug', message, null, additionalData);
  }

  /**
   * Setup global error handlers
   */
  setupGlobalErrorHandlers() {
    // Cattura errori JavaScript non gestiti
    window.addEventListener('error', (event) => {
      this.logError(
        `Unhandled Error: ${event.message}`,
        event.error,
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        }
      );
    });

    // Cattura promise rejection non gestite
    window.addEventListener('unhandledrejection', (event) => {
      this.logError(
        `Unhandled Promise Rejection: ${event.reason}`,
        event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        {
          type: 'unhandledrejection'
        }
      );
    });

    // Cattura errori di rete (opzionale)
    window.addEventListener('offline', () => {
      this.logWarning('Network connection lost', {
        type: 'network',
        online: false
      });
    });

    window.addEventListener('online', () => {
      this.logInfo('Network connection restored', {
        type: 'network',
        online: true
      });
    });
  }
}

// Export singleton instance
export const logService = new LogService();
